/**
 * Route table for the kanban API client.
 *
 * The authenticated board talks to the per-resource controllers; the public board (a board token
 * instead of a user session) talks to `OpenProjectApiController`, whose templates mirror the
 * authenticated ones under one prefix (server Task 7). Every URL the client sends therefore
 * differs only in its prefix, and `kanbanPaths` is the single place that spells the paths.
 *
 * Five builders are the exception to "mirror": `exportTimeLogs`, `createCustomField`, `customField`,
 * `customFieldOrder` and `customFieldValuesBatch` are reachable only from authenticated-only screens
 * today, and `OpenProjectApiController` has no matching route for any of them (the time-log export is
 * a deliberate carve-out; the custom-field admin routes simply were never mirrored). Calling their
 * `pub` form would 404. See kanbanRoutes.test.ts's `AUTHENTICATED_ONLY_NO_PUBLIC_ROUTE` table.
 */
export type KanbanMode = 'authenticated' | 'public';

export type KanbanRoutes = {
  mode: KanbanMode;
  tasks: string;
  statuses: string;
  customFields: string;
  customFieldValues: string;
};

export const AUTHENTICATED_ROUTES: KanbanRoutes = {
  mode: 'authenticated',
  tasks: '/api/KanbanTask',
  statuses: '/api/KanbanTaskStatus',
  customFields: '/api/CustomField',
  customFieldValues: '/api/CustomFieldValue',
};

export const PUBLIC_ROUTES: KanbanRoutes = {
  mode: 'public',
  tasks: '/api/OpenProjectApi',
  statuses: '/api/OpenProjectApi/status',
  customFields: '/api/OpenProjectApi/custom-field',
  customFieldValues: '/api/OpenProjectApi/custom-field-value',
};

export function routesForMode(mode: KanbanMode): KanbanRoutes {
  return mode === 'public' ? PUBLIC_ROUTES : AUTHENTICATED_ROUTES;
}

/** Every URL the kanban client sends, as a function of the route table. */
export type KanbanPaths = {
  tasksByProject(projectId: string): string;
  deletedTasksByProject(projectId: string): string;
  task(taskIdOrPublicId: string, projectId?: string): string;
  createTask(): string;
  restoreTask(taskIdOrPublicId: string): string;
  restoreBulk(): string;
  deleteBulk(): string;
  permanentDelete(taskIdOrPublicId: string): string;
  permanentDeleteBulk(): string;
  timeMetrics(taskId: string): string;
  estimatedTime(taskId: string): string;
  logTime(taskId: string): string;
  timeLogs(taskId: string): string;
  move(taskId: string): string;
  order(taskIdOrPublicId: string): string;
  changes(taskIdOrPublicId: string): string;
  status(taskIdOrPublicId: string): string;
  title(taskIdOrPublicId: string): string;
  description(taskIdOrPublicId: string): string;
  scheduledAndPriority(taskIdOrPublicId: string): string;
  /** Without `assigneeId` the task is unassigned (`{assigneeId?}` is optional on the server). */
  assignee(taskIdOrPublicId: string, assigneeId?: string): string;
  comments(taskIdOrPublicId: string): string;
  comment(commentId: string): string;
  images(taskIdOrPublicId: string): string;
  uploadAttachment(confirmation: boolean): string;
  attachments(taskIdOrPublicId: string): string;
  attachment(taskIdOrPublicId: string, publicId: string): string;
  exportTimeLogs(): string;
  /** The board token's project (`PublicProjectDto`); public mode only. */
  project(): string;
  statusesByProject(projectId: string): string;
  createStatus(): string;
  statusById(statusId: string): string;
  deleteStatus(statusId: string, moveTasksTo?: number): string;
  statusOrder(statusId: string): string;
  customFieldsByProject(projectId: string): string;
  createCustomField(): string;
  customField(fieldId: string): string;
  customFieldOrder(fieldId: string): string;
  customFieldValuesByTask(taskId: string): string;
  customFieldValue(taskId: string, fieldId: string): string;
  customFieldValuesBatch(taskId: string): string;
};

const seg = (value: string | number): string => encodeURIComponent(String(value));

/** Cloudinary public ids contain slashes that the server's `{*publicId}` catch-all expects raw. */
const publicIdPath = (publicId: string): string => publicId.split('/').map(encodeURIComponent).join('/');

export function kanbanPaths(routes: KanbanRoutes): KanbanPaths {
  const { tasks, statuses, customFields, customFieldValues } = routes;
  return {
    tasksByProject: (projectId) => `${tasks}/project/${seg(projectId)}`,
    deletedTasksByProject: (projectId) => `${tasks}/project/${seg(projectId)}/deleted`,
    task: (taskIdOrPublicId, projectId) =>
      `${tasks}/${seg(taskIdOrPublicId)}${projectId ? `?projectId=${seg(projectId)}` : ''}`,
    createTask: () => tasks,
    restoreTask: (taskIdOrPublicId) => `${tasks}/restore/${seg(taskIdOrPublicId)}`,
    restoreBulk: () => `${tasks}/restore/bulk`,
    deleteBulk: () => `${tasks}/bulk`,
    permanentDelete: (taskIdOrPublicId) => `${tasks}/permanent/${seg(taskIdOrPublicId)}`,
    permanentDeleteBulk: () => `${tasks}/permanent/bulk`,
    timeMetrics: (taskId) => `${tasks}/${seg(taskId)}/time`,
    estimatedTime: (taskId) => `${tasks}/${seg(taskId)}/estimated-time`,
    logTime: (taskId) => `${tasks}/${seg(taskId)}/log-time`,
    timeLogs: (taskId) => `${tasks}/${seg(taskId)}/time-logs`,
    move: (taskId) => `${tasks}/${seg(taskId)}/move`,
    order: (taskIdOrPublicId) => `${tasks}/${seg(taskIdOrPublicId)}/order`,
    changes: (taskIdOrPublicId) => `${tasks}/${seg(taskIdOrPublicId)}/changes`,
    status: (taskIdOrPublicId) => `${tasks}/${seg(taskIdOrPublicId)}/status`,
    title: (taskIdOrPublicId) => `${tasks}/${seg(taskIdOrPublicId)}/title`,
    description: (taskIdOrPublicId) => `${tasks}/${seg(taskIdOrPublicId)}/description`,
    scheduledAndPriority: (taskIdOrPublicId) => `${tasks}/${seg(taskIdOrPublicId)}/scheduledAndPriority`,
    assignee: (taskIdOrPublicId, assigneeId) =>
      `${tasks}/${seg(taskIdOrPublicId)}/assignee${assigneeId ? `/${seg(assigneeId)}` : ''}`,
    comments: (taskIdOrPublicId) => `${tasks}/${seg(taskIdOrPublicId)}/comments`,
    comment: (commentId) => `${tasks}/comments/${seg(commentId)}`,
    images: (taskIdOrPublicId) => `${tasks}/${seg(taskIdOrPublicId)}/images`,
    uploadAttachment: (confirmation) => `${tasks}/UploadKanbanTaskAttachment${confirmation ? '?confirmation=true' : ''}`,
    attachments: (taskIdOrPublicId) => `${tasks}/${seg(taskIdOrPublicId)}/attachments`,
    attachment: (taskIdOrPublicId, publicId) => `${tasks}/${seg(taskIdOrPublicId)}/attachments/${publicIdPath(publicId)}`,
    exportTimeLogs: () => `${tasks}/ExportProjectTimeLogs`,
    project: () => `${tasks}/project`,
    statusesByProject: (projectId) => `${statuses}/project/${seg(projectId)}`,
    createStatus: () => statuses,
    statusById: (statusId) => `${statuses}/${seg(statusId)}`,
    deleteStatus: (statusId, moveTasksTo) =>
      `${statuses}/${seg(statusId)}${moveTasksTo === undefined ? '' : `?moveTasksTo=${seg(moveTasksTo)}`}`,
    statusOrder: (statusId) => `${statuses}/${seg(statusId)}/order`,
    customFieldsByProject: (projectId) => `${customFields}/project/${seg(projectId)}`,
    createCustomField: () => customFields,
    customField: (fieldId) => `${customFields}/${seg(fieldId)}`,
    customFieldOrder: (fieldId) => `${customFields}/${seg(fieldId)}/order`,
    customFieldValuesByTask: (taskId) => `${customFieldValues}/task/${seg(taskId)}`,
    customFieldValue: (taskId, fieldId) => `${customFieldValues}/task/${seg(taskId)}/field/${seg(fieldId)}`,
    customFieldValuesBatch: (taskId) => `${customFieldValues}/task/${seg(taskId)}/batch`,
  };
}
